import React, { useState, useMemo } from "react";
import {
  FiSearch,
  FiClock,
  FiTag,
  FiChevronRight,
  FiX,
  FiCheck,
  FiUser,
  FiStar,
} from "../../lib/icons";
import {
  TaskTemplate,
  TASK_TEMPLATES,
  getTemplateCategories,
  searchTemplates,
} from "../../lib/task-templates";
import { Project } from "../../lib/adhd-todo-manager";

interface TemplateSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: TaskTemplate, projectId?: string) => void;
  projects?: Project[];
}

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
  projects = [],
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>();

  const categories = getTemplateCategories();

  const filteredTemplates = useMemo(() => {
    let templates = TASK_TEMPLATES;

    if (selectedCategory) {
      templates = templates.filter(t => t.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      templates = searchTemplates(searchQuery);
    }

    return templates;
  }, [selectedCategory, searchQuery]);

  const handleTemplateSelect = (template: TaskTemplate) => {
    setSelectedTemplate(template);
  };

  const handleConfirmTemplate = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate, selectedProjectId);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedCategory(null);
    setSearchQuery("");
    setSelectedTemplate(null);
    setSelectedProjectId(undefined);
    onClose();
  };

  const getDifficultyColor = (difficulty?: string) => {
    switch (difficulty) {
      case "easy":
        return "text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400";
      case "medium":
        return "text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400";
      case "hard":
        return "text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400";
      default:
        return "text-gray-600 bg-gray-100 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-4xl max-h-[90vh] bg-white dark:bg-gray-900 rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
              <FiStar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Task Templates
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Choose a pre-made template to get started quickly
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="flex h-[600px]">
          {/* Sidebar - Categories */}
          <div className="w-64 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
            <div className="p-4">
              {/* Search */}
              <div className="relative mb-4">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                    setSelectedCategory(null); // Clear category filter when searching
                  }}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Categories */}
              <div className="space-y-1">
                <button
                  onClick={() => {
                    setSelectedCategory(null);
                    setSearchQuery("");
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                    selectedCategory === null
                      ? "bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  <span className="text-lg">📋</span>
                  <span className="flex-1 text-left">All Templates</span>
                  <span className="text-xs bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded">
                    {TASK_TEMPLATES.length}
                  </span>
                </button>

                {categories.map(category => (
                  <button
                    key={category.key}
                    onClick={() => {
                      setSelectedCategory(category.key);
                      setSearchQuery("");
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                      selectedCategory === category.key
                        ? "bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    }`}
                  >
                    <span className="text-lg">{category.icon}</span>
                    <span className="flex-1 text-left">{category.label}</span>
                    <span className="text-xs bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded">
                      {category.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content - Template List */}
          <div className="flex-1 flex">
            {/* Template List */}
            <div className="w-1/2 overflow-y-auto border-r border-gray-200 dark:border-gray-700">
              <div className="p-4">
                <div className="space-y-3">
                  {filteredTemplates.map(template => (
                    <div
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedTemplate?.id === template.id
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/10"
                          : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{template.icon}</span>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 dark:text-white truncate">
                            {template.name}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                            {template.description}
                          </p>

                          <div className="flex items-center gap-2 mt-3">
                            {template.estimatedTime && (
                              <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                                <FiClock className="w-3 h-3" />
                                {template.estimatedTime}
                              </span>
                            )}
                            {template.difficulty && (
                              <span
                                className={`text-xs px-2 py-1 rounded capitalize ${getDifficultyColor(template.difficulty)}`}
                              >
                                {template.difficulty}
                              </span>
                            )}
                            <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                              {template.tasks.length} tasks
                            </span>
                          </div>
                        </div>
                        <FiChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      </div>
                    </div>
                  ))}

                  {filteredTemplates.length === 0 && (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                        <FiSearch className="w-6 h-6 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        No templates found
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400">
                        Try adjusting your search or category filter
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Template Preview */}
            <div className="w-1/2 overflow-y-auto">
              {selectedTemplate ? (
                <div className="p-6">
                  <div className="mb-6">
                    <div className="flex items-start gap-4 mb-4">
                      <span className="text-3xl">{selectedTemplate.icon}</span>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                          {selectedTemplate.name}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          {selectedTemplate.description}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {selectedTemplate.estimatedTime && (
                        <span className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded">
                          <FiClock className="w-4 h-4" />
                          {selectedTemplate.estimatedTime}
                        </span>
                      )}
                      {selectedTemplate.difficulty && (
                        <span
                          className={`text-sm px-3 py-1 rounded capitalize ${getDifficultyColor(selectedTemplate.difficulty)}`}
                        >
                          {selectedTemplate.difficulty}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-1 mb-6">
                      {selectedTemplate.tags.map(tag => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/20 px-2 py-1 rounded"
                        >
                          <FiTag className="w-3 h-3" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Tasks Preview */}
                  <div className="mb-6">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                      Tasks ({selectedTemplate.tasks.length})
                    </h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {selectedTemplate.tasks.map((task, index) => (
                        <div
                          key={index}
                          className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-5 h-5 rounded border-2 border-gray-300 dark:border-gray-600 flex-shrink-0 mt-0.5"></div>
                            <div className="flex-1 min-w-0">
                              <h5 className="font-medium text-gray-900 dark:text-white text-sm">
                                {task.title}
                              </h5>
                              {task.description && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                  {task.description}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                {task.estimatedTime && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {task.estimatedTime}min
                                  </span>
                                )}
                                {task.priority && task.priority !== "medium" && (
                                  <span
                                    className={`text-xs px-1.5 py-0.5 rounded ${
                                      task.priority === "urgent" || task.priority === "high"
                                        ? "text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400"
                                        : "text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400"
                                    }`}
                                  >
                                    {task.priority}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Project Selection */}
                  {projects.length > 0 && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Add to Project (Optional)
                      </label>
                      <select
                        value={selectedProjectId || ""}
                        onChange={e => setSelectedProjectId(e.target.value || undefined)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">No project</option>
                        {projects.map(project => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleConfirmTemplate}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                    >
                      <FiCheck className="w-4 h-4" />
                      Use This Template
                    </button>
                    <button
                      onClick={handleClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-6 h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                      <FiUser className="w-6 h-6 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Select a template
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400">
                      Choose a template from the list to see its details and tasks
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateSelector;
